<template>
  <div>
    <div class="flex relative mx-auto mb-4" style="max-width: 960px">
      <div class="pl-20 pr-10 box-border" style="width: 240px">
        <ul class="fixed text-gray-62 text-sm">
          <li
            @click="scrollTo('#companyDetail1')"
            class="mb-4 cursor-pointer"
            :class="{
              'text-gray-92 font-semibold': state.visibleSection === 1,
            }"
          >
            企業概要
          </li>
          <li
            @click="scrollTo('#companyDetail2')"
            class="mb-4 cursor-pointer"
            :class="{
              'text-gray-92 font-semibold': state.visibleSection === 2,
            }"
          >
            経営データ
          </li>
          <li
            @click="scrollTo('#companyDetail3')"
            class="mb-4 cursor-pointer"
            :class="{
              'text-gray-92 font-semibold': state.visibleSection === 3,
            }"
          >
            所在地
          </li>
        </ul>
      </div>

      <div>
        <section class="pr-20" style="max-width: 720px">
          <div class="mb-16">
            <span class="block text-gray-92 font-semibold text-2xl mb-8">
              {{ detail.name }}
            </span>
            <div class="flex justify-between items-start mb-4">
              <div style="width: calc(100% - 258px)">
                <span
                  class="block text-gray-92 font-light leading-7"
                  style="font-size: 15px; margin-bottom: 38px"
                >
                  {{ detail.address }}
                </span>
                <div class="flex" style="margin-bottom: 10px">
                  <span
                    class="text-gray-92 text-sm font-semibold whitespace-no-wrap"
                    style="width: 100px"
                    >企業サイト</span
                  >
                  <a
                    :href="detail.url"
                    target="_blank"
                    class="text-gray-62 text-sm"
                    style="width: calc(100% - 100px)"
                  >
                    {{ detail.url }}
                  </a>
                </div>
                <div v-if="detail.inquiry_url" class="flex">
                  <span
                    class="text-gray-92 text-sm font-semibold whitespace-no-wrap"
                    style="width: 100px"
                    >お問い合わせ</span
                  >
                  <a
                    :href="detail.inquiry_url"
                    target="_blank"
                    class="text-gray-62 text-sm"
                    style="width: calc(100% - 100px)"
                  >
                    {{ detail.inquiry_url }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-16">
            <div
              v-if="detail.new_pdf_file"
              class="flex items-center justify-center mb-4 bg-slate-100 border-dashed border"
              style="width: 640px; height: 450px"
            >
              <div class="max-w-full max-h-full m-auto">
                （ アップした企業紹介資料PDFが表示されます ）
              </div>
            </div>
            <CompanyImageSlider
              v-else-if="detail.info_images && detail.info_images.length > 0"
              :images="detail.info_images"
              :companyId="Number(detail.id)"
            />
            <div
              v-else
              class="flex items-center justify-center mb-4 bg-slate-100 border-dashed border"
              style="width: 640px; height: 450px"
            >
              <div class="max-w-full max-h-full m-auto">
                （ アップする企業紹介資料PDFが表示されます ）
              </div>
            </div>
          </div>

          <div class="mb-16">
            <h4
              id="companyDetail1"
              class="block text-gray-92 font-semibold text-xl leading-8 mb-6"
            >
              企業概要
            </h4>
            <div>
              <div
                v-for="(item, key) in detail.summary_items"
                v-show="hasValue(item.values)"
                :key="'summary' + key"
                class="flex items-center border-b border-gray-12"
                style="min-height: 56px; padding: 18px 8px"
              >
                <span class="text-gray-62 text-sm w-4/12 mr-4">{{
                  item.name
                }}</span>
                <div class="text-gray-92 text-sm leading-5 w-8/12">
                  <span
                    v-for="(value, key2) in item.values"
                    :key="`summary${key}_${key2}`"
                  >
                    {{
                      item.display_format_type === 'number'
                        ? amountFormatter(value.value)
                        : value.value
                    }}{{ item.unit && value.value !== '-' ? item.unit : ''
                    }}{{ item.values.length - 1 != key2 ? ' / ' : '' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-16">
            <h4
              id="companyDetail2"
              class="block text-gray-92 font-semibold text-xl leading-8 mb-6"
            >
              経営データ
            </h4>
            <div>
              <div
                v-for="(item, key) in detail.management_items"
                v-show="hasValue(item.values)"
                :key="'management' + key"
                class="flex items-center border-b border-gray-12"
                style="min-height: 56px; padding: 18px 8px"
              >
                <span class="text-gray-62 text-sm w-4/12 mr-4">{{
                  item.name
                }}</span>
                <div class="text-gray-92 text-sm leading-5 w-8/12">
                  <span
                    v-for="(value, key2) in item.values"
                    :key="`management${key}_${key2}`"
                  >
                    {{
                      item.display_format_type === 'number'
                        ? amountFormatter(value.value)
                        : value.value
                    }}{{ item.unit && value.value !== '-' ? item.unit : ''
                    }}{{ item.values.length - 1 != key2 ? ' / ' : '' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-16">
            <h4
              id="companyDetail3"
              class="block text-gray-92 font-semibold text-xl leading-8 mb-6"
            >
              企業所在地
            </h4>
            <div>
              <div
                v-for="(item, key) in state.formattedOffices"
                :key="'office' + key"
                class="flex items-center border-b border-gray-12"
                style="min-height: 56px; padding: 18px 8px"
              >
                <span class="text-gray-62 text-sm w-4/12 mr-4">{{
                  item.type
                }}</span>
                <span class="text-gray-92 text-sm leading-5 w-8/12">{{
                  item.address
                }}</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, computed, onMounted } from 'vue';
import { amountFormatter } from '@/utils/filters.js';
import CompanyImageSlider from '@/components/modalRight/service/CompanyImageSlider.vue';

export default {
  props: {
    detail: Object,
  },
  components: {
    CompanyImageSlider,
  },
  setup(props) {
    const state = reactive({
      visibleSection: 1,
      formattedOffices: computed(() => {
        const array = [];
        const offices = props.detail.offices || [];
        for (let i = 0, branch = 1; i < offices.length; i++) {
          if (offices[i].type === 'head') {
            array.push({ type: '本社', address: offices[i].address });
          } else if (offices[i].type === 'branch') {
            array.push({ type: `支社${branch}`, address: offices[i].address });
            branch++;
          }
        }
        return array;
      }),
    });
    const hasValue = (values) => {
      const input = values.find((e) => e.value !== null && e.value !== '');
      return input ? true : false;
    };
    const scrollTo = (id) => {
      const modal = document.getElementById('modalRight');
      const target = document.getElementById(id.replace('#', ''));
      modal.scrollTop = target.offsetTop;
    };
    onMounted(() => {
      const modal = document.getElementById('modalRight');
      const target = [
        document.getElementById('companyDetail1'),
        document.getElementById('companyDetail2'),
        document.getElementById('companyDetail3'),
      ];
      modal.onscroll = () => {
        const scrollTop = modal.scrollTop;
        for (let i = target.length; i > 0; i--) {
          if (scrollTop >= target[i - 1].offsetTop) {
            state.visibleSection = i;
            break;
          }
        }
      };
    });
    return {
      state,
      hasValue,
      scrollTo,
      amountFormatter,
    };
  },
};
</script>

<style lang="scss" scoped></style>
